import { useCallback, useEffect, useRef } from 'react'
import { MS_IN_SEC } from 'shared/constants'
import { useUtcDayReset } from 'shared/hooks/use-utc-day-reset'
import { useVisibilityChange } from 'shared/hooks/use-visibility-change'
import type { DemoCoinBalance } from './use-demo-coin-balance'

/** The sign-up bonus lands a moment after the account exists: a first 0 is asked again this much later. */
export const FIRST_ZERO_RETRY_MS = 5 * MS_IN_SEC

/** The daily top-up job starts at 00:00 UTC on the server: the balance is asked this much later. */
export const TOP_UP_SETTLE_MS = 10 * MS_IN_SEC

type Refetch = DemoCoinBalance['refetch']

/** `refetch` now or in `delay` ms, as it is then; a pending one is dropped on unmount. */
const useRefetchLater = (refetch: Refetch) => {
  const refetchRef = useRef(refetch)
  const timersRef = useRef(new Set<number>())

  useEffect(() => {
    refetchRef.current = refetch
  }, [refetch])

  useEffect(() => {
    const timers = timersRef.current

    return () => timers.forEach(timer => window.clearTimeout(timer))
  }, [])

  const refetchNow = useCallback(() => void refetchRef.current(), [])

  const refetchIn = useCallback(
    (delay: number) => {
      const timer = window.setTimeout(() => {
        timersRef.current.delete(timer)
        refetchNow()
      }, delay)

      timersRef.current.add(timer)
    },
    [refetchNow]
  )

  return { refetchNow, refetchIn }
}

/** Calls `onBack` each time the tab is on screen again after it was hidden. */
const useOnTabBack = (onBack: () => void) => {
  const isVisible = useVisibilityChange()
  const wasVisibleRef = useRef(isVisible)

  useEffect(() => {
    const wasVisible = wasVisibleRef.current
    wasVisibleRef.current = isVisible

    if (isVisible && !wasVisible) onBack()
  }, [isVisible, onBack])
}

/** Calls `onZero` when the first known balance of a signed-in player is 0. */
const useOnFirstZero = (
  { isEnabled, balance }: Pick<DemoCoinBalance, 'isEnabled' | 'balance'>,
  onZero: () => void
) => {
  const isFirstAnswerRef = useRef(true)

  useEffect(() => {
    if (!isEnabled) {
      isFirstAnswerRef.current = true
      return
    }

    if (balance === null || !isFirstAnswerRef.current) return

    isFirstAnswerRef.current = false

    if (balance === 0) onZero()
  }, [balance, isEnabled, onZero])
}

/**
 * Keeps every reader of the balance fresh from one always-mounted place (the
 * header chip), so N readers set up no N timers: reads the balance again
 * (`refetch`, every reader) after the 00:00 UTC top-up, when the tab is back
 * on screen, and once more when the first answer after a sign-in is 0 (the
 * sign-up bonus may still be on its way). Nothing for a guest.
 */
export const useDemoCoinBalanceRefresh = ({
  isEnabled,
  balance,
  refetch
}: Pick<DemoCoinBalance, 'isEnabled' | 'balance' | 'refetch'>) => {
  const { refetchNow, refetchIn } = useRefetchLater(refetch)

  const retryZero = useCallback(
    () => refetchIn(FIRST_ZERO_RETRY_MS),
    [refetchIn]
  )

  useUtcDayReset(() => refetchIn(TOP_UP_SETTLE_MS), { isTicking: false })
  // NOTE: a round may have settled, a top-up landed or a task been claimed elsewhere meanwhile
  useOnTabBack(refetchNow)
  useOnFirstZero({ isEnabled, balance }, retryZero)
}
